import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { checkIsAdmin } from "@/lib/admin.functions";
import {
  previewUtilityEmail,
  previewAuthEmail,
  sendUtilityEmailTest,
  type UtilityTemplateKey,
} from "@/lib/email/email-test-bench.functions";
import { EMAIL_CATALOG, CATALOG_TOTAL, type CatalogEmail } from "@/lib/email/email-catalog";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Loader2, Clock, Users, Zap, Repeat, Eye, Send } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin_/email-preview")({
  component: EmailPreviewPage,
  head: () => ({
    meta: [
      { title: "E-postöversikt – Tack. admin" },
      { name: "description", content: "Alla mejl Tack skickar, när de skickas och till vem." },
    ],
  }),
});

type PreviewState = { html?: string; subject?: string; to?: string; variant?: string };

function EmailPreviewPage() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [merchantId, setMerchantId] = useState("");
  const [recipientOverride, setRecipientOverride] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  type RecentRow = {
    message_id: string | null;
    template_name: string | null;
    recipient_email: string | null;
    status: string | null;
    error_message: string | null;
    created_at: string;
  };
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const selected: CatalogEmail | null = useMemo(() => {
    for (const g of EMAIL_CATALOG) {
      const hit = g.emails.find((e) => e.id === selectedId);
      if (hit) return hit;
    }
    return null;
  }, [selectedId]);

  const loadRecent = async () => {
    setRecentLoading(true);
    try {
      const { data, error } = await supabase
        .from("email_send_log")
        .select("message_id, template_name, recipient_email, status, error_message, created_at")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      const byMsg = new Map<string, RecentRow>();
      for (const row of (data ?? []) as RecentRow[]) {
        const key = row.message_id ?? `${row.recipient_email}-${row.created_at}`;
        if (!byMsg.has(key)) byMsg.set(key, row);
      }
      setRecent(Array.from(byMsg.values()).slice(0, 25));
    } catch (e) {
      console.error("Failed to load recent emails", e);
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    checkIsAdmin().then((r) => setIsAdmin(r.isAdmin)).catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    supabase
      .from("merchants")
      .select("id")
      .eq("user_id", user.id)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.id) setMerchantId(data.id);
      });
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const loadPreview = async (entry: CatalogEmail) => {
    setSelectedId(entry.id);
    setPreview({});

    if (entry.preview.kind === "none") {
      toast.info(entry.preview.reason);
      return;
    }

    setLoading(true);
    setPreviewOpen(true);
    try {
      if (entry.preview.kind === "utility") {
        const res = await previewUtilityEmail({ data: { key: entry.preview.key as UtilityTemplateKey } });
        setPreview({ html: res.html, subject: res.subject });
      } else if (entry.preview.kind === "auth") {
        const res = await previewAuthEmail({ data: { type: entry.preview.type } });
        if (!res.html) throw new Error("Mallen kunde inte renderas");
        setPreview({ html: res.html, subject: res.subject ?? undefined });
      } else if (entry.preview.kind === "edge_merchant") {
        if (!merchantId) {
          toast.error("Ange ett merchant-ID ovan");
          return;
        }
        const body: Record<string, unknown> = { merchant_id: merchantId, dry_run: true, force: true };
        if (recipientOverride.trim()) body.recipient_override = recipientOverride.trim();
        const { data, error } = await supabase.functions.invoke(entry.preview.fn, { body });
        if (error) throw error;
        setPreview({ html: data.html, subject: data.subject, to: data.to, variant: data.variant });
      } else if (entry.preview.kind === "edge_dunning") {
        if (!user?.id) return;
        const { data, error } = await supabase.functions.invoke("send-dunning-email", {
          body: {
            dry_run: true,
            user_id: user.id,
            provider_subscription_id: "sub_preview",
            variant: entry.preview.variant,
            failure_cycle_started_at: new Date().toISOString(),
          },
        });
        if (error) throw error;
        setPreview({ html: data.html, subject: data.subject, to: data.to });
      } else if (entry.preview.kind === "edge_dry") {
        const body: Record<string, unknown> = { dry_run: true };
        if (entry.preview.previewKey) body.preview_key = entry.preview.previewKey;
        const { data, error } = await supabase.functions.invoke(entry.preview.fn, { body });
        if (error) throw error;
        setPreview({ html: data.html, subject: data.subject });
      }
    } catch (e) {
      console.error(e);
      setPreviewOpen(false);
      toast.error(e instanceof Error ? e.message : "Kunde inte ladda förhandsvisning");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    if (!selected) return;
    const target = recipientOverride.trim();
    if (!target) {
      toast.error("Ange en mottagaradress ovan");
      return;
    }
    const shownTarget = selected.preview.kind === "utility" ? (user?.email ?? "din e-post") : target;
    if (!confirm(`Skicka testmejl (${selected.label}) till ${shownTarget}?`)) return;

    setSending(true);
    try {
      if (selected.preview.kind === "utility") {
        const res = await sendUtilityEmailTest({
          data: { key: selected.preview.key as UtilityTemplateKey, recipient: target },
        });
        if (!res.ok) throw new Error(res.error);
      } else if (selected.preview.kind === "edge_merchant") {
        if (!merchantId) throw new Error("Merchant-ID saknas");
        const { error } = await supabase.functions.invoke(selected.preview.fn, {
          body: { merchant_id: merchantId, force: true, recipient_override: target },
        });
        if (error) throw error;
      } else if (selected.preview.kind === "edge_dunning") {
        const { error } = await supabase.functions.invoke("send-dunning-email", {
          body: {
            user_id: user?.id,
            provider_subscription_id: "sub_preview",
            variant: selected.preview.variant,
            failure_cycle_started_at: new Date().toISOString(),
            recipient_override: target,
          },
        });
        if (error) throw error;
      } else {
        toast.info("Det här mejlet kan bara förhandsvisas, inte testskickas.");
        return;
      }
      toast.success(`Testmejl skickat till ${target}`);
      loadRecent();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sändning misslyckades");
    } finally {
      setSending(false);
    }
  };

  if (isAdmin === null) {
    return <div className="p-8 text-muted-foreground">Laddar…</div>;
  }
  if (!isAdmin) {
    return <div className="p-8 text-muted-foreground">Endast admin.</div>;
  }

  const canTestSend =
    selected != null &&
    (selected.preview.kind === "utility" ||
      selected.preview.kind === "edge_merchant" ||
      selected.preview.kind === "edge_dunning");

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-display font-bold">E-postöversikt</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Alla {CATALOG_TOTAL} mejl Tack kan skicka, vad som utlöser dem, när de går ut och till vem.
          Kundernas meddelanden (rabattkod, påminnelse, kampanj) skickas som SMS och finns inte här.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inställningar för förhandsvisning</CardTitle>
          <p className="text-sm text-muted-foreground">
            Merchant-ID används för livscykelmejlen (de hämtar riktig data). Mottagaradressen används för
            testutskick — lämna tomt för att bara förhandsvisa.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mid">Merchant-ID</Label>
            <Input
              id="mid"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              placeholder="uuid…"
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="recipient">Skicka testmejl till</Label>
            <Input
              id="recipient"
              value={recipientOverride}
              onChange={(e) => setRecipientOverride(e.target.value)}
              placeholder="din@epost.se"
            />
          </div>
        </CardContent>
      </Card>

      {EMAIL_CATALOG.map((group) => (
        <Card key={group.id}>
          <CardHeader>
            <CardTitle className="text-base">{group.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{group.description}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {group.emails.map((e) => {
              const isSelected = e.id === selectedId;
              return (
                <div
                  key={e.id}
                  className={`rounded-lg border p-4 space-y-3 ${isSelected ? "border-primary/60 bg-primary/[0.03]" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1">
                      <div className="font-semibold">{e.label}</div>
                      <code className="text-[11px] text-muted-foreground">{e.logLabel}</code>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant={isSelected ? "default" : "outline"} onClick={() => loadPreview(e)}>
                        {isSelected && loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" /> Visa
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <div className="flex gap-2">
                      <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{e.audience}</span>
                    </div>
                    <div className="flex gap-2">
                      <Zap className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{e.trigger}</span>
                    </div>
                    <div className="flex gap-2">
                      <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{e.timing}</span>
                    </div>
                    <div className="flex gap-2">
                      <Repeat className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{e.frequency}</span>
                    </div>
                  </div>

                  {e.notes && <p className="text-xs text-muted-foreground italic">{e.notes}</p>}

                  {e.preview.kind === "none" && (
                    <Badge variant="secondary" className="text-[11px] font-normal">
                      Ingen förhandsvisning: {e.preview.reason}
                    </Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Förhandsvisning: {selected?.label ?? ""}</DialogTitle>
            <div className="text-sm space-y-1 text-left">
              {preview.to && (
                <div>
                  <span className="text-muted-foreground">Till:</span> {preview.to}
                </div>
              )}
              {preview.subject && (
                <div>
                  <span className="text-muted-foreground">Ämne:</span> <strong>{preview.subject}</strong>
                </div>
              )}
              {preview.variant && (
                <div>
                  <span className="text-muted-foreground">Variant:</span> {preview.variant}
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4">
            {canTestSend && (
              <Button variant="outline" size="sm" onClick={sendTest} disabled={sending || !preview.html}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Skicka testmejl</>}
              </Button>
            )}
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Renderar…
              </div>
            ) : !preview.html ? (
              <p className="text-sm text-muted-foreground">
                Mallen returnerade ingen HTML. Kontrollera merchant-ID:t och försök igen.
              </p>
            ) : (
              <iframe
                srcDoc={preview.html}
                sandbox=""
                className="w-full border rounded-md bg-white"
                style={{ height: "70vh" }}
                title={`${selected?.label ?? "email"} preview`}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>


      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Senaste e-postaktivitet</CardTitle>
            <p className="text-sm text-muted-foreground">Alla mallar, avdubblat på message-id.</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadRecent} disabled={recentLoading}>
            {recentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Uppdatera"}
          </Button>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen aktivitet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-1 pr-3">När</th>
                    <th className="text-left py-1 pr-3">Mall</th>
                    <th className="text-left py-1 pr-3">Mottagare</th>
                    <th className="text-left py-1 pr-3">Status</th>
                    <th className="text-left py-1">Orsak</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => {
                    const statusClass =
                      r.status === "sent" ? "text-green-600" :
                      r.status === "dlq" || r.status === "failed" || r.status === "bounced" || r.status === "complained" ? "text-red-600" :
                      r.status === "suppressed" ? "text-yellow-600" :
                      "text-muted-foreground";
                    return (
                      <tr key={`${r.message_id ?? i}-${r.created_at}`} className="border-b last:border-0">
                        <td className="py-1 pr-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="py-1 pr-3">{r.template_name ?? "—"}</td>
                        <td className="py-1 pr-3 font-mono">{r.recipient_email ?? "—"}</td>
                        <td className={`py-1 pr-3 font-semibold ${statusClass}`}>{r.status ?? "—"}</td>
                        <td className="py-1 text-muted-foreground max-w-[28rem] truncate" title={r.error_message ?? ""}>
                          {r.error_message ?? ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
